import {
  Boondock,
  FlowStepType,
  FlowType,
  GUEST_FLOW_RV,
  GUEST_FLOW_TENT,
  HelperFunctions,
  HOST_BOONDOCK_FLOW,
  HOST_SINGLESITE_FLOW,
  IBoondock,
  IListing,
  IPolicy,
  IRV,
  ISignUpFlowStep,
  ISite,
  IUser,
  Listing,
  MULTISITE_FLOW,
  Role,
  SALES_SINGLESITE_FLOW,
  Site,
} from '@curbnturf/entities';
import { toPrecision } from '@curbnturf/helpers';
import * as SiteActions from '@curbnturf/site/src/lib/+state/site.actions';
import { Action, createReducer, on } from '@ngrx/store';
import * as SignUpStateActions from './sign-up-state.actions';
import { SignUpStateState } from './sign-up-state.actions';

const { findListingLatestStep, findBoondockLatestStep } = HelperFunctions;

export const initialState: SignUpStateState = {
  spinner: false,
  steps: [],
  user: {},
};

const signUpStateReducer = createReducer(
  initialState,
  on(SignUpStateActions.setState, (state, action) => {
    return {
      ...state,
      ...{ ...action, type: undefined }, // don't add the action type to the state.
      percentComplete: percentCompleteCalculation(action.steps, action.currentStep),
      redirect: undefined,
    };
  }),
  on(SignUpStateActions.redirect, (state, action) => {
    return {
      ...state,
      redirect: action.routeTo,
    };
  }),
  on(SignUpStateActions.startHostFlow, (state, action) => {
    const steps = [...HOST_SINGLESITE_FLOW];
    const currentStep = HOST_SINGLESITE_FLOW.slice(0, 1).shift();
    return {
      ...state,
      steps: [...HOST_SINGLESITE_FLOW],
      currentStep: HOST_SINGLESITE_FLOW.slice(0, 1).shift(),
      percentComplete: percentCompleteCalculation(steps, currentStep),
      user: { ...state.user, ...action.user, role: Role.host },
    };
  }),
  on(SignUpStateActions.startGuestFlow, (state, action) => {
    const steps = [...GUEST_FLOW_RV];
    const currentStep = GUEST_FLOW_RV.slice(0, 1).shift();
    return {
      ...state,
      steps,
      currentStep,
      percentComplete: percentCompleteCalculation(steps, currentStep),
      user: { ...state.user, ...action.user, role: Role.guest },
    };
  }),
  on(SignUpStateActions.switchWizardFlow, (state, action) => {
    const steps = [...action.flow];
    const currentStep = HOST_SINGLESITE_FLOW.slice(0, 1).shift();

    let newCurrentStep: ISignUpFlowStep | undefined = undefined;
    const originalCurrentStep = state.currentStep;
    if (originalCurrentStep) {
      const stepFound = steps.find((el) => el.type === originalCurrentStep?.type);
      if (stepFound) {
        newCurrentStep = stepFound;
      } else {
        newCurrentStep = HOST_SINGLESITE_FLOW.slice(0, 1).shift();
      }
    }

    return {
      ...state,
      steps,
      currentStep: newCurrentStep,
      percentComplete: percentCompleteCalculation(steps, currentStep),
    };
  }),
  on(SignUpStateActions.saveState, (state) => {
    return {
      ...state,
      spinner: true,
    };
  }),
  on(SignUpStateActions.updateInProgressListingId, (state) => {
    const user = {
      ...state.user,
      inProgressId: state.listing?.id,
    };

    return {
      ...state,
      user,
    };
  }),
  on(SignUpStateActions.saveStateSuccess, (state, action) => {
    const user = { ...state.user, ...action.user };
    let listing: IListing | undefined;
    if (action.listing !== null && action.listing !== undefined) {
      listing = { ...state.listing, ...action.listing };
    }

    if (listing && user.id) {
      listing.user = { ...listing.user, id: user.id };
    }

    let boondock: IBoondock | undefined;
    if (action.boondock) {
      boondock = { ...state.boondock, ...action.boondock };
    }

    if (boondock && user.id) {
      boondock.user = { ...boondock.user, id: user.id };
    }

    if (action.listing && action.listing.id) {
      user.inProgressId = action.listing.id;
    }

    if (action.user && !action.user.selectedRv && action?.user?.rvs && action.user.rvs.length > 0) {
      const selectedRv = action.user.rvs.slice().shift();
      if (selectedRv) {
        user.selectedRv = selectedRv;
      }
    }

    return {
      ...state,
      user,
      listing,
      spinner: false,
    };
  }),
  on(SignUpStateActions.saveUserState, (state, action) => {
    const user = { ...state.user, ...action.user };

    if (action.user && !action.user.selectedRv && action?.user?.rvs && action.user.rvs.length > 0) {
      const selectedRv = action.user.rvs.slice().shift();
      if (selectedRv) {
        user.selectedRv = selectedRv;
      }
    }

    return {
      ...state,
      user,
      spinner: false,
    };
  }),
  on(SignUpStateActions.saveStateFailed, (state) => {
    return {
      ...state,
      spinner: false,
    };
  }),
  on(SignUpStateActions.setCurrentStep, (state, action) => {
    return {
      ...state,
      currentStep: action.step,
      percentComplete: percentCompleteCalculation(state.steps, action.step),
      spinner: false,
    };
  }),
  on(SignUpStateActions.setListingTypeHost, (state, action) => {
    let steps: ISignUpFlowStep[];
    let currentSite: ISite | undefined = undefined;
    if (state.listing?.sites && Array.isArray(state.listing.sites)) {
      if (state.listing.sites.length > 1) {
        steps = [...MULTISITE_FLOW];
        currentSite = state.listing.sites.slice().shift() as ISite;
      } else if (state.listing.sites.length === 1) {
        steps = [...HOST_SINGLESITE_FLOW];
        currentSite = state.listing.sites.slice().shift() as ISite;
      } else {
        steps = [...HOST_SINGLESITE_FLOW];
      }
    } else {
      steps = [...HOST_SINGLESITE_FLOW];
    }
    const currentStep = steps.find((el) => el.type === FlowStepType.RoleSelector);
    return {
      ...state,
      steps,
      currentStep,
      percentComplete: percentCompleteCalculation(steps, currentStep),
      user: { ...state.user, role: Role.host },
      listing: { ...state.listing, ...action.listing },
      selectedSite: currentSite,
    };
  }),
  on(SignUpStateActions.setListingTypeHostSuccess, (state, action) => {
    return {
      ...state,
      user: { ...action.user, inProgressId: action.listing?.id },
      listing: { ...action.listing },
    };
  }),
  on(SignUpStateActions.setBoondockPropertyTypeStep, (state, action) => {
    const boondock = { ...state.boondock, landTypes: action.landTypes };

    return {
      ...state,
      boondock,
    };
  }),
  on(SignUpStateActions.setBoondockStepSuccess, (state, action) => {
    return {
      ...state,
      user: { ...action.user, inProgressId: action.boondock?.id },
      boondock: { ...action.boondock },
    };
  }),
  on(SignUpStateActions.saveSourceStep, (state, action) => {
    const user = { ...state.user, source: action.user.source };

    return {
      ...state,
      user,
    };
  }),
  on(SignUpStateActions.savePhoneStep, (state, action) => {
    const user = { ...state.user, phone: action.user.phone, sendSmss: action.user.sendSmss };

    return {
      ...state,
      user,
    };
  }),
  on(SignUpStateActions.savePropertyTypeStep, (state, action) => {
    const listing = { ...state.listing, landTypes: action.types };

    return {
      ...state,
      listing,
    };
  }),
  on(SignUpStateActions.saveSiteStateSuccess, (state, action) => {
    if (state.listing?.sites && state.listing.sites.length >= 1 && action.site) {
      const foundIndex = state.listing?.sites?.findIndex((el) => el.id === action.site?.id);
      if (foundIndex === -1) {
        return {
          ...state,
          spinner: false,
        };
      }

      const sites: ISite[] = state.listing.sites.slice();
      sites.splice(foundIndex, 1, action.site);

      return {
        ...state,
        listing: {
          ...state.listing,
          sites,
        },
        selectedSite: action.site,
        spinner: false,
      };
    }
    if (!action.site) {
      return {
        ...state,
        selectedSite: undefined,
        spinner: false,
      };
    }
    return {
      ...state,
      spinner: false,
    };
  }),
  on(SignUpStateActions.enableSiteSuccess, (state, action) => {
    const sites: ISite[] = [...(state.listing?.sites ?? [])];

    const foundIndex = sites.findIndex((site) => site.id === action.site?.id);
    if (foundIndex) {
      const updatedSite = new Site({ ...sites[foundIndex] });
      updatedSite.available = true;
      sites.splice(foundIndex, 1, updatedSite);
    }

    return {
      ...state,
      listing: new Listing({
        ...state.listing,
        sites: sites,
      }),
    };
  }),
  on(SignUpStateActions.disableSiteSuccess, (state, action) => {
    const sites: ISite[] = [...(state.listing?.sites ?? [])];

    const foundIndex = sites.findIndex((site) => site.id === action.site?.id);
    if (foundIndex) {
      const updatedSite = new Site({ ...sites[foundIndex] });
      updatedSite.available = false;
      sites.splice(foundIndex, 1, updatedSite);
    }

    return {
      ...state,
      listing: new Listing({
        ...state.listing,
        sites: sites,
      }),
    };
  }),
  on(SignUpStateActions.saveBookingStep, (state, action) => {
    const listing: IListing = {
      ...state.listing,
      checkIn: action.checkIn,
      checkOut: action.checkOut,
      minLengthOfStayInDays: action.minLengthOfStayInDays,
      maxLengthOfStayInDays: action.maxLengthOfStayInDays,
      reserveDaysInAdvance: action.reserveDaysInAdvance,
      bookingWindow: action.bookingWindow,
      checkInProcess: action.checkInProcess,
      reservationProcess: action.reservationProcess,
    };

    return {
      ...state,
      listing,
    };
  }),
  on(SignUpStateActions.savePoliciesStep, (state, action) => {
    const listing: IListing = {
      ...state.listing,
      policies: action.policies?.map((el: IPolicy) => ({ ...el, listingId: state.listing?.id })),
    };

    return {
      ...state,
      listing,
    };
  }),
  on(SignUpStateActions.saveLocationStep, (state, action) => {
    const listing: IListing = {
      ...state.listing,
      address: action.address,
      coordinates: action.coordinates,
      landOwner: action.landOwner,
    };

    return {
      ...state,
      listing,
    };
  }),
  on(SignUpStateActions.startUserRvFlow, (state) => {
    const steps = [...GUEST_FLOW_RV];
    const currentStep = steps.find((el) => el.type === FlowStepType.UserGarage);
    return {
      ...state,
      currentStep,
      percentComplete: percentCompleteCalculation(steps, currentStep),
      steps,
    };
  }),
  on(SignUpStateActions.startUserTentFlow, (state) => {
    const steps = [...GUEST_FLOW_TENT];
    const currentStep = steps.find((el) => el.type === FlowStepType.UserGarage);
    return {
      ...state,
      currentStep,
      percentComplete: percentCompleteCalculation(steps, currentStep),
      steps,
    };
  }),
  on(SignUpStateActions.saveUserRvStep, (state, action) => {
    let rvs: IRV[] = state.user?.rvs?.slice() || [];

    // Check for a selected RV first - in case we are repeating this step.
    let updatedRv: IRV;
    let selectedRv: IRV | undefined;
    if (state.user?.selectedRv?.id) {
      rvs = state.user?.rvs?.filter((el) => el.id !== action.rv?.id) || [];
      updatedRv = { ...state.user.selectedRv, ...action.rv };
      selectedRv = { ...state.user.selectedRv, ...action.rv };
    } else {
      updatedRv = action.rv;
      selectedRv = undefined;
    }

    const user = {
      ...state.user,
      rvs: [...rvs, updatedRv],
      selectedRv,
    };

    return {
      ...state,
      user,
    };
  }),
  on(SignUpStateActions.saveUserTentStep, (state, action) => {
    let rvs: IRV[] = state.user?.rvs?.slice() || [];
    const foundRv = rvs.find((el) => el.id === action.rv?.id);

    // Check for a selected RV first - in case we are repeating this step.
    let selectedRv: IRV | undefined;
    rvs = state.user?.rvs?.filter((el) => el.id !== action.rv?.id) || [];
    if (state.user?.selectedRv?.id) {
      rvs.push({ ...foundRv, ...action.rv });
      selectedRv = { ...state.user.selectedRv, ...action.rv };
    } else {
      rvs.push({ ...foundRv, ...action.rv });
      selectedRv = undefined;
    }

    const user = {
      ...state.user,
      rvs,
      selectedRv,
    };

    return {
      ...state,
      user,
    };
  }),
  on(SignUpStateActions.saveUserRvSizeStep, (state, action) => {
    let rvs: IRV[] = state.user?.rvs?.slice() || [];

    // Check for a selected RV first - in case we are repeating this step.
    let selectedRv: IRV | undefined;
    let updatedRv: IRV;
    if (state.user?.selectedRv?.id) {
      updatedRv = { ...state.user.selectedRv, ...action.rv };
      rvs = state.user?.rvs?.filter((el) => el.id !== updatedRv.id) || [];
      rvs.push(updatedRv);
      selectedRv = updatedRv;
    } else {
      rvs.push({ ...action.rv });
      selectedRv = undefined;
    }

    const user = {
      ...state.user,
      rvs,
      selectedRv,
    };

    return {
      ...state,
      user,
    };
  }),
  on(SignUpStateActions.saveUserRoadConditionStep, (state, action) => {
    let rvs: IRV[] = state.user?.rvs?.slice() || [];

    // Check for a selected RV first - in case we are repeating this step.
    let selectedRv: IRV | undefined;
    let updatedRv: IRV;
    if (state.user?.selectedRv?.id) {
      updatedRv = { ...state.user.selectedRv, ...action.rv };
      rvs = state.user?.rvs?.filter((el) => el.id !== updatedRv.id) || [];
      rvs.push(updatedRv);
      selectedRv = updatedRv;
    } else {
      rvs.push({ ...action.rv });
      selectedRv = undefined;
    }

    const user = {
      ...state.user,
      rvs,
      selectedRv,
    };

    return {
      ...state,
      user,
    };
  }),
  on(SignUpStateActions.saveDescriptionStep, (state, action) => {
    const listing: IListing = {
      ...state.listing,
      name: action.name,
      description: action.description,
    };

    return {
      ...state,
      listing,
    };
  }),
  on(SignUpStateActions.updateProfilePhoto, (state, action) => {
    const user: IUser = {
      ...state.user,
      photo: action.photo,
    };

    return {
      ...state,
      user,
    };
  }),
  on(SignUpStateActions.updateListingPhotos, (state, action) => {
    const listing: IListing = {
      ...state.listing,
      photos: [...action.photos],
    };

    return {
      ...state,
      listing,
    };
  }),
  on(SignUpStateActions.updateBoondockPhotos, (state, action) => {
    const boondock: IBoondock = {
      ...state.boondock,
      photos: [...action.photos],
    };

    return {
      ...state,
      boondock,
    };
  }),
  on(SignUpStateActions.savePhotosStep, (state, action) => {
    const listing: IListing = {
      ...state.listing,
      photos: [...(action.photos || [])],
    };

    return {
      ...state,
      listing,
    };
  }),
  on(SignUpStateActions.removeSite, (state, action) => {
    const listing = state.listing;
    const sites = state.listing?.sites?.slice() ?? [];

    const foundIndex = sites.findIndex((site) => site.id === action.site.id);
    if (foundIndex !== -1) {
      sites.splice(foundIndex, 1);
    }

    let selectedSite = state.selectedSite;
    if (selectedSite?.id === action.site.id) {
      selectedSite = undefined;
    }

    return {
      ...state,
      listing: {
        ...listing,
        sites,
      },
      selectedSite,
    };
  }),
  on(SignUpStateActions.duplicateSiteSuccess, (state, action) => {
    const listing = state.listing;
    const sites: ISite[] = state.listing?.sites?.slice() ?? [];
    if (action.site && action.site.id) {
      sites.unshift(action.site);
    }

    return {
      ...state,
      listing: {
        ...listing,
        sites,
      },
    };
  }),
  on(SiteActions.siteImagesUpdated, (state, action) => {
    const listing = state.listing;
    const sites = state.listing?.sites?.slice() ?? [];
    let newSite: ISite;
    let selectedSite = state.selectedSite;

    const foundIndex = sites.findIndex((site) => site.id === action.id);
    if (foundIndex !== -1) {
      newSite = new Site({ ...sites[foundIndex], photos: action.photos });
      sites.splice(foundIndex, 1, newSite);

      if (selectedSite?.id === action.id) {
        selectedSite = newSite;
      }
    }

    return {
      ...state,
      listing: {
        ...listing,
        sites,
      },
      selectedSite,
    };
  }),
  on(SignUpStateActions.addNewSiteSuccess, (state, action) => {
    return {
      ...state,
      listing: action.listing,
    };
  }),
  on(SignUpStateActions.restoreUserSuccess, (state, action) => {
    const currentUser = action.currentUser;
    const steps = [];
    let currentStep: ISignUpFlowStep | undefined;
    let flow: FlowType;
    if (action.user.role === Role.guest) {
      if (action.user.selectedRv) {
        if (action.user.selectedRv.tent) {
          steps.push(...GUEST_FLOW_TENT);
          currentStep = GUEST_FLOW_TENT.slice(0, 1).shift();
        } else {
          steps.push(...GUEST_FLOW_RV);
          currentStep = GUEST_FLOW_RV.slice(0, 1).shift();
        }
      } else {
        steps.push(...GUEST_FLOW_RV);
        currentStep = GUEST_FLOW_RV.slice(0, 1).shift();
      }
      flow = FlowType.guest;
    } else if (action.user.role === Role.boondocker) {
      steps.push(...HOST_BOONDOCK_FLOW);
      currentStep = HOST_BOONDOCK_FLOW.slice(0, 1).shift();
      flow = FlowType.boondock;
    } else if (
      currentUser &&
      (currentUser.role === Role.sales || (currentUser.role?.toLowerCase() as Role) === Role.salesManager)
    ) {
      // Switching to sales
      steps.push(...SALES_SINGLESITE_FLOW);
      currentStep = SALES_SINGLESITE_FLOW.slice(0, 1).shift();
      flow = FlowType.sales;
    } else {
      // defaulting to host
      steps.push(...HOST_SINGLESITE_FLOW);
      currentStep = HOST_SINGLESITE_FLOW.slice(0, 1).shift();
      flow = FlowType.listing;
    }

    // Figure out what the current step should be
    const latestStep = findListingLatestStep(flow, state.listing, action.user);
    if (latestStep) {
      const foundStep = steps.find((el) => el.type === latestStep);
      if (foundStep) {
        currentStep = foundStep;
      }
    }

    if (!currentStep) {
      currentStep = state.currentStep;
    }

    return {
      ...state,
      user: { ...state.user, ...action.user },
      steps,
      currentStep,
      percentComplete: percentCompleteCalculation(steps, currentStep),
    };
  }),
  on(SignUpStateActions.restoreListingSuccess, (state, action) => {
    const currentUser = action.currentUser;
    const listing = action.listing;
    const flow: FlowType = FlowType.listing;
    const steps = [];
    let currentStep: ISignUpFlowStep | undefined;
    if (
      currentUser &&
      (currentUser.role === Role.sales || (currentUser.role?.toLowerCase() as Role) === Role.salesManager)
    ) {
      steps.push(...SALES_SINGLESITE_FLOW);
      currentStep = SALES_SINGLESITE_FLOW.slice(0, 1).shift();
    } else {
      steps.push(...HOST_SINGLESITE_FLOW);
      currentStep = HOST_SINGLESITE_FLOW.slice(0, 1).shift();
    }

    // Figure out what the current step should be
    const site = listing?.sites?.slice().shift();
    const latestStep = findListingLatestStep(flow, listing, state.user, site);
    if (latestStep) {
      const foundStep = steps.find((el) => el.type === latestStep);
      if (foundStep) {
        currentStep = foundStep;
      }
    }

    if (!currentStep) {
      currentStep = state.currentStep;
    }

    return {
      ...state,
      listing: { ...state.listing, ...listing },
      selectedSite: site,
      steps,
      currentStep,
      percentComplete: percentCompleteCalculation(steps, currentStep),
    };
  }),
  on(SignUpStateActions.displaySpinner, (state) => {
    return {
      ...state,
      spinner: true,
    };
  }),
  on(SignUpStateActions.hideSpinner, (state) => {
    return {
      ...state,
      spinner: false,
    };
  }),
  on(SignUpStateActions.resetProcess, () => {
    return {
      currentStep: undefined,
      percentComplete: 0,
      redirect: undefined,
      user: {},
      boondock: {},
      listing: {},
      spinner: false,
      steps: [],
    };
  }),
  on(SignUpStateActions.setInProgressListing, (state, action) => {
    let user = state.user;
    if (action.listing?.id) {
      user = { ...state.user, inProgressId: action.listing.id };
    }

    return {
      ...state,
      user,
    };
  }),
  on(SignUpStateActions.startNewListingAndFlow, (state, action) => {
    const currentUser = action.currentUser;
    const user: IUser = {
      ...state.user,
      ...action.user,
    };

    // this is a special case for sales users who are starting a new listing for a different user.
    if (
      currentUser &&
      (currentUser.role === Role.sales || (currentUser.role?.toLowerCase() as Role) === Role.salesManager) &&
      currentUser.id !== user.id
    ) {
      const listing: IListing = new Listing();

      let steps = [];
      let currentStep;
      user.role = Role.host;
      steps.push(...SALES_SINGLESITE_FLOW);

      const latestStep = findListingLatestStep(FlowType.sales, listing, user);

      if (latestStep) {
        const foundStep = steps.find((el) => el.type === latestStep);
        if (foundStep) {
          currentStep = foundStep;
        }
      }

      return {
        currentStep,
        percentComplete: percentCompleteCalculation(steps, currentStep),
        redirect: undefined,
        user,
        listing,
        spinner: false,
        steps,
      };
    } else {
      const listing: IListing = new Listing();

      let steps = [];
      let currentStep;
      steps.push(...HOST_SINGLESITE_FLOW);

      const latestStep = findListingLatestStep(FlowType.listing, listing, user);

      if (latestStep) {
        const foundStep = steps.find((el) => el.type === latestStep);
        if (foundStep) {
          currentStep = foundStep;
        }
      }

      return {
        currentStep,
        percentComplete: percentCompleteCalculation(steps, currentStep),
        redirect: undefined,
        user,
        listing,
        spinner: false,
        steps,
      };
    }
  }),
  on(SignUpStateActions.startNewListingSuccess, (state, action) => {
    const user = { ...state.user, ...action.user };
    const listing = { ...state.listing, ...action.listing };

    if (user.id) {
      listing.user = user;
    }

    return {
      ...state,
      user,
      listing,
      spinner: false,
    };
  }),
  on(SignUpStateActions.saveClientProfileStepSuccess, (state, action) => {
    const user = action.user;
    return {
      ...state,
      user,
      listing: {
        user,
      },
    };
  }),
  on(SignUpStateActions.startNewBoondock, (state, action) => {
    const boondock: IBoondock = new Boondock();

    let role;
    let steps = [];
    let currentStep;
    role = Role.host;
    steps.push(...HOST_BOONDOCK_FLOW);

    const user: IUser = {
      ...state.user,
      ...action.user,
      role,
    };

    const flow: FlowType = FlowType.boondock;
    const latestStep = findBoondockLatestStep(flow, boondock, user);

    if (latestStep) {
      const foundStep = steps.find((el) => el.type === latestStep);
      if (foundStep) {
        currentStep = foundStep;
      }
    }

    return {
      currentStep,
      percentComplete: percentCompleteCalculation(steps, currentStep),
      redirect: undefined,
      user,
      boondock,
      spinner: false,
      steps,
    };
  }),
  on(SignUpStateActions.startNewBoondockSuccess, (state, action) => {
    const user = { ...state.user, ...action.user };
    const boondock = { ...state.boondock, ...action.boondock };

    if (user.id) {
      boondock.user = user;
    }

    return {
      ...state,
      user,
      boondock,
      spinner: false,
    };
  }),
  on(SignUpStateActions.selectSiteFromListing, (state, action) => {
    return {
      ...state,
      selectedSite: action.site,
    };
  }),
);

export function reducer(state: SignUpStateState | undefined, action: Action) {
  return signUpStateReducer(state, action);
}

function percentCompleteCalculation(steps: ISignUpFlowStep[], currentStep?: ISignUpFlowStep): number {
  if (!steps || !currentStep) {
    return 0;
  }

  const foundIndex = steps.findIndex((el) => el.type === currentStep?.type);
  return foundIndex ? toPrecision((foundIndex / (steps.length - 1)) * 100, 0) : 0;
}
